import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@nextui-org/react';
import { CtyButton, CtyShowToast, CtySpinner, checkType } from '@circuitry-ai/doc-ui';
import {
  useGetAiWorkerByIdQuery,
  useGetEvaluationsPresignedUrlMutation,
  useTypedSelector,
  useUpdateAiWorkersMutation,
} from '@circuitry-ai/doc-data';
import { parseDate } from '@internationalized/date';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAnalystDashboardDateRange } from './analystDateRangeSession';
import {
  PYANALYST_ENDPOINT_CONFIG_ATTR,
  applyDateFiltersToPayload,
  extractDateFiltersFromPayload,
  parsePyAnalystEndpointConfig,
  resolvePyAnalystHeaders,
  type PyAnalystDateFilter,
  type PyAnalystEndpointConfig,
  type PyAnalystReportResponse,
} from './pyanalystUtils';

function dateFiltersToPickerState(filters: PyAnalystDateFilter[]) {
  if (filters.length === 0) return null;
  const primary = filters[0];
  return {
    dateFrom: parseDate(primary.from),
    dateTo: parseDate(primary.to),
    column: primary.column,
  };
}

export function PyanalystDetails() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const aiworkerId = searchParams.get('aiworkerId') ?? '';
  const token = useTypedSelector((state) => state.auth.token);
  const { report: reportFromState } = location?.state ?? {};

  const { data: analystFromQuery } = useGetAiWorkerByIdQuery(aiworkerId, {
    skip: !aiworkerId || !!reportFromState,
    refetchOnMountOrArgChange: true,
  });

  const analystReport = reportFromState ?? analystFromQuery;

  const endpointConfig = useMemo(() => {
    const configAttr = analystReport?.aiworkers_attrs?.find(
      (attr: { attr_name?: string }) => attr.attr_name === PYANALYST_ENDPOINT_CONFIG_ATTR
    );
    return parsePyAnalystEndpointConfig(configAttr?.attr_json);
  }, [analystReport]);

  const initialDates = useMemo(
    () => dateFiltersToPickerState(extractDateFiltersFromPayload(endpointConfig?.sample_payload)),
    [endpointConfig]
  );
  const configuredRange = useMemo(
    () => (initialDates ? { dateFrom: initialDates.dateFrom, dateTo: initialDates.dateTo } : null),
    [initialDates]
  );
  const { dateFrom, dateTo, setDateFrom, setDateTo } = useAnalystDashboardDateRange(configuredRange);
  const [dateColumn, setDateColumn] = useState(initialDates?.column ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [presignedUrl, setPresignedUrl] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [getEvaluationsPresignedUrl] = useGetEvaluationsPresignedUrlMutation();
  const [updateAiWorkers] = useUpdateAiWorkersMutation();
  const hasAutoFetchedRef = useRef(false);

  useEffect(() => {
    if (!initialDates?.column) return;
    setDateColumn(initialDates.column);
  }, [initialDates]);

  const isDateRangeInvalid = !!dateFrom && !!dateTo && dateFrom.toString() > dateTo.toString();

  const dateToErrorMessage = (() => {
    if (isDateRangeInvalid) {
      return t('Date To should be greater than or equal to Date From');
    }
    if (hasSubmitted && !dateTo) {
      return t('Date To is required');
    }
    return '';
  })();

  const persistSamplePayload = useCallback(
    async (config: PyAnalystEndpointConfig, samplePayload: Record<string, unknown>) => {
      if (!aiworkerId) return;
      const updatedConfig = { ...config, sample_payload: samplePayload };
      try {
        await updateAiWorkers({
          aiworker_id: aiworkerId,
          payLoad: {
            aiworkers_attrs: [
              {
                attr_name: PYANALYST_ENDPOINT_CONFIG_ATTR,
                attr_value: null,
                attr_json: updatedConfig,
              },
            ],
          },
        }).unwrap();
      } catch {
        /* attrs sync is best-effort; report view still works */
      }
    },
    [aiworkerId, updateAiWorkers]
  );

  const fetchPyAnalystReport = async () => {
    setHasSubmitted(true);
    setErrorMessage('');

    if (!endpointConfig?.function_url) {
      CtyShowToast(checkType.error, t('PyAnalyst endpoint configuration is missing'));
      return;
    }

    if (!dateFrom || !dateTo) {
      CtyShowToast(checkType.error, t('Please select both Date From and Date To'));
      return;
    }

    if (dateFrom.toString() > dateTo.toString()) {
      CtyShowToast(checkType.error, t('Date From should be less than or equal to Date To'));
      return;
    }

    if (!token) {
      CtyShowToast(checkType.error, t('Authentication is not ready yet'));
      return;
    }

    setIsLoading(true);
    try {
      const basePayload = (endpointConfig.sample_payload ?? {}) as Record<string, unknown>;
      const existingFilters = extractDateFiltersFromPayload(basePayload);
      const column = dateColumn || existingFilters[0]?.column;
      if (!column) {
        throw new Error(t('Date filter column is not configured for this analyst'));
      }

      const dateFilters: PyAnalystDateFilter[] = [
        { column, from: dateFrom.toString(), to: dateTo.toString() },
        ...existingFilters.slice(1),
      ];
      const requestPayload = applyDateFiltersToPayload(basePayload, dateFilters);

      const method = (endpointConfig.method ?? 'POST').toUpperCase();
      const headers = {
        'Content-Type': 'application/json',
        ...resolvePyAnalystHeaders(endpointConfig.headers, token),
      };

      const reportResponse = await fetch(endpointConfig.function_url, {
        method,
        headers,
        ...(method !== 'GET' ? { body: JSON.stringify(requestPayload) } : {}),
      });

      if (!reportResponse.ok) {
        throw new Error(t('Unable to fetch PyAnalyst report'));
      }

      const reportPayload = (await reportResponse.json()) as PyAnalystReportResponse;
      const firstItem = reportPayload.data?.[0];
      const evaluationId = firstItem?.analyst_id;
      const s3Key = firstItem?.s3_key;

      if (!evaluationId || !s3Key) {
        throw new Error(reportPayload.message || t('PyAnalyst response is invalid'));
      }

      await persistSamplePayload(endpointConfig, requestPayload);

      const presignedPayload = await getEvaluationsPresignedUrl({
        evaluation_id: evaluationId,
        s3_key: s3Key,
      }).unwrap();
      const generatedUrl = presignedPayload?.data?.presigned_url;

      if (!generatedUrl) {
        throw new Error(t('Presigned URL is missing in response'));
      }

      setPresignedUrl(generatedUrl);
    } catch (error) {
      const message =
        (error as Error)?.message || t('Something went wrong while fetching PyAnalyst report');
      setErrorMessage(message);
      CtyShowToast(checkType.error, message);
      setPresignedUrl('');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!endpointConfig || hasAutoFetchedRef.current) return;
    hasAutoFetchedRef.current = true;
    void fetchPyAnalystReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointConfig]);

  if (!endpointConfig && !analystReport) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-140px)] min-h-[480px]">
        <CtySpinner size="lg" />
      </div>
    );
  }

  if (!endpointConfig) {
    return <p className="text-sm text-danger">{t('PyAnalyst endpoint configuration is missing')}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row flex-wrap gap-3 items-end">
        <DatePicker
          size="sm"
          label={t('Date Range')}
          labelPlacement="outside"
          value={dateFrom}
          onChange={(date) => setDateFrom(date)}
          isInvalid={hasSubmitted && !dateFrom}
          errorMessage={hasSubmitted && !dateFrom ? t('Date From is required') : ''}
          className="w-full sm:w-[150px]"
        />
        <DatePicker
          size="sm"
          labelPlacement="outside"
          value={dateTo}
          onChange={(date) => setDateTo(date)}
          isInvalid={(hasSubmitted && !dateTo) || isDateRangeInvalid}
          errorMessage={dateToErrorMessage}
          className="w-full sm:w-[150px]"
        />
        <CtyButton
          id="fetch-pyanalyst-report"
          color="primary"
          className="w-full sm:w-[140px]"
          onPress={fetchPyAnalystReport}
          isLoading={isLoading}
        >
          {t('View Report')}
        </CtyButton>
      </div>

      {isLoading && !presignedUrl && (
        <div className="flex justify-center items-center h-[calc(100vh-140px)] min-h-[480px]">
          <CtySpinner size="lg" />
        </div>
      )}

      {!isLoading && errorMessage && (
        <p className="text-sm text-danger">{errorMessage}</p>
      )}

      {presignedUrl && (
        <div className="h-[calc(100vh-140px)] min-h-[480px]">
          <iframe
            src={presignedUrl}
            title={t('PyAnalyst Report')}
            className="w-full h-full border-0"
          />
        </div>
      )}
    </div>
  );
}

export default PyanalystDetails;
